import { createApi } from "@reduxjs/toolkit/query/react";
import { adminBaseQuery } from "@store/baseQuery";
import { UserDoctorResource } from "@/entities/users/api";

// Type definitions based on swagger schema
export interface PaymentProvider {
  name: string;
  display_name: string;
}

export enum OrganizationStatus {
  PAID = 'paid',
  TRIAL = 'trial',
  EXTENDED = 'extended',
  CANCELED = 'canceled'
}

export const ORGANIZATION_STATUS_OPTIONS = Object.values(OrganizationStatus).map(status => ({
  value: status,
  label: status.charAt(0).toUpperCase() + status.slice(1)
}));

export const getOrganizationStatusColor = (status: OrganizationStatus): string => {
  switch (status) {
    case OrganizationStatus.PAID: return 'green';
    case OrganizationStatus.TRIAL: return 'blue';
    case OrganizationStatus.EXTENDED: return 'purple';
    case OrganizationStatus.CANCELED: return 'red';
    default: return 'gray';
  }
};

export interface Organization {
  id: number;
  name: string;
  timezone: string;
  subscription_ends_at: string | null;
  trial_ends_at: string | null;
  status: OrganizationStatus;
  payment_provider_id: number | null;
  payment_provider: PaymentProvider | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// User profile interface for organization users
export interface UserProfileShort {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  slug: string | null;
  active: boolean;
  status: string;
  local_trial_end_date: string;
  title: string | null;
  profile: {
    id: number;
    occupation: string;
    status: string;
  };
  roles: string[];
}

export interface CreateOrganizationRequest {
  name: string;
  timezone?: string;
  payment_provider?: string;
}

export interface UpdateOrganizationRequest {
  name: string;
  timezone?: string;
  payment_provider?: string;
}

export interface ErrorResponse {
  status: boolean;
  message: string;
  errors?: Record<string, string[]>;
}

export interface OrganizationConfig {
  show_condition_groups: boolean | null;
  show_condition_search: boolean | null;
  redirect_speedbump_to_condition_page: boolean | null;
  show_assistant_doctor_picker: boolean | null;
  show_enrolments: boolean | null;
  enable_dashboard_chat?: boolean | null;
  opt_out_enrollment_consent: boolean | null;
  whitelabel?: any;
}

export interface UpdateOrganizationConfigRequest {
  show_condition_groups?: boolean | null;
  show_condition_search?: boolean | null;
  redirect_speedbump_to_condition_page?: boolean | null;
  show_assistant_doctor_picker?: boolean | null;
  show_enrolments?: boolean | null;
  enable_dashboard_chat?: boolean | null;
  opt_out_enrollment_consent?: boolean | null;
}

// Query parameters interface for filtering and searching
export interface OrganizationQueryParams {
  search?: string;
  status?: string;
  payment_provider?: string;
  per_page?: number;
  page?: number;
}

// Paginated response interface
export interface PaginatedOrganizationsResponse {
  current_page: number;
  data: Organization[];
  last_page: number;
  per_page: number;
  total: number;
}

export const organizationsApi = createApi({
  reducerPath: "organizationsApi",
  baseQuery: adminBaseQuery,
  tagTypes: ["Organization"],
  endpoints: (builder) => ({
    // Get all organizations with filtering and pagination
    getOrganizations: builder.query<PaginatedOrganizationsResponse, OrganizationQueryParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();

        // Add query parameters if they exist and params is not void
        if (params?.search) searchParams.append('search', params.search);
        if (params?.status) searchParams.append('status', params.status);
        if (params?.payment_provider) searchParams.append('payment_provider', params.payment_provider);
        if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
        if (params?.page) searchParams.append('page', params.page.toString());

        const queryString = searchParams.toString();
        return `/organizations${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ["Organization"],
    }),

    // Get a specific organization
    getOrganization: builder.query<Organization, number>({
      query: (id) => `/organizations/${id}`,
      providesTags: (result, error, id) => [
        { type: "Organization", id },
      ],
    }),

    // Create a new organization
    createOrganization: builder.mutation<
      Organization,
      CreateOrganizationRequest
    >({
      query: (data) => ({
        url: "/organizations",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Organization"],
    }),

    // Update a specific organization
    updateOrganization: builder.mutation<
      Organization,
      { id: number; data: UpdateOrganizationRequest }
    >({
      query: ({ id, data }) => ({
        url: `/organizations/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Organization", id },
        "Organization",
      ],
    }),

    // Delete a specific organization
    deleteOrganization: builder.mutation<
      { organization_id: number },
      number
    >({
      query: (id) => ({
        url: `/organizations/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Organization"],
    }),

    // Get doctors of an organization
    getOrganizationDoctors: builder.query<UserDoctorResource[], number>({
      query: (id) => `/organizations/${id}/doctors`,
      providesTags: (result, error, id) => [
        { type: "Organization", id: `${id}-doctors` },
      ],
    }),

    // Get assistants of an organization
    getOrganizationAssistants: builder.query<any[], number>({
      query: (id) => `/organizations/${id}/assistants`,
      providesTags: (result, error, id) => [
        { type: "Organization", id: `${id}-assistants` },
      ],
    }),

    // Get all users of an organization
    getOrganizationUsers: builder.query<UserProfileShort[], number>({
      query: (id) => `/organizations/${id}/users`,
      providesTags: (result, error, id) => [
        { type: "Organization", id: `${id}-users` },
      ],
    }),

    // Get config for an organization
    getConfig: builder.query<OrganizationConfig, number>({
      query: (id) => `/organizations/${id}/config`,
      providesTags: (result, error, id) => [{ type: "Organization", id }],
    }),

    // Update config for an organization
    updateConfig: builder.mutation<OrganizationConfig, { id: number } & UpdateOrganizationConfigRequest>({
      query: ({ id, ...data }) => ({
        url: `/organizations/${id}/config`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Organization", id },
        "Organization",
      ],
    }),

  }),
});

export const {
  useGetOrganizationsQuery,
  useLazyGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetOrganizationDoctorsQuery,
  useGetOrganizationAssistantsQuery,
  useGetOrganizationUsersQuery,
  useGetConfigQuery,
  useUpdateConfigMutation,
} = organizationsApi; 
